import { find } from 'lodash';
import { useMemo } from 'react';

import { CareCategories, SectionType } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon'; // Adjust the path as needed
import { useLagoon } from '../../hooks/useLagoon'; // Adjust the path as needed

/**
 * Returns the popover content text based on sectionType and smartChoiceAvailabilityDescription.
 */
export const GetPopoverContentBySectionType = (
  sectionType: string
): string | null => {
  const lagoonData = useLagoon('ui-messaging')();
  const smartChoiceAvailabilityDescription = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_AVAILABILITY_DESCRIPTION,
  });
  const smartChoiceAvailabilityProviderGroup = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_AVAILABILITY_PROVIDER_GROUP,
  });
  const smartChoiceAvailabilityFacilityDescription = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_AVAILABILITY_FACILITY_DESCRIPTION,
  });
  switch (sectionType?.toLowerCase()) {
    case SectionType?.FACILITY?.toLowerCase():
      return smartChoiceAvailabilityFacilityDescription?.message; // Replace with actual content if needed
    case SectionType?.PROVIDER_GROUP?.toLowerCase():
      return smartChoiceAvailabilityProviderGroup?.message; // Replace with actual content if needed
    case SectionType?.PROVIDER?.toLowerCase():
      return smartChoiceAvailabilityDescription?.message;
    default:
      return null;
  }
};

/**
 * React hook to memoize popover content by section type.
 */
export function usePopoverContentBySectionType(
  sectionType: string
): string | null {
  return useMemo(
    () => GetPopoverContentBySectionType(sectionType),
    [sectionType]
  );
}

export function isValidCareCategoryWithSectionType(
  chipValue: string | undefined,
  sectionType: string | undefined
): boolean {
  const invalidChips = [
    CareCategories.ALL,
    CareCategories.MEDICAL_GROUPS,
    CareCategories.MEDICAL_SPECIALISTS,
    CareCategories.PRIMARY_CARE,
    CareCategories.MEDICAL,
  ];
  // If sectionType is undefined, return true for all chips
  if (!sectionType) {
    return true;
  }

  const sectionTypeLower = sectionType.toLowerCase();

  // For invalid chips
  if (invalidChips.includes(chipValue as CareCategories)) {
    if (sectionTypeLower === SectionType.PROVIDER?.toLowerCase()) {
      return true;
    }
    if (
      sectionTypeLower === SectionType.FACILITY?.toLowerCase() ||
      sectionTypeLower === SectionType.PROVIDER_GROUP?.toLowerCase()
    ) {
      return true;
    }
  }

  // For valid chips, use the original logic
  return !invalidChips.includes(chipValue as CareCategories);
}

export function isValidCareCategory(
  careCategory: string | undefined,
  sectionType?: string | undefined
): boolean {
  const invalidCategories = [
    CareCategories.ALL,
    CareCategories.MEDICAL_GROUPS,
    CareCategories.MEDICAL_SPECIALISTS,
    CareCategories.PRIMARY_CARE,
    CareCategories.MEDICAL,
  ];

  const sectionTypeLower = sectionType?.toLowerCase();

  // If careCategory is invalid but sectionType is PROVIDER_GROUP or FACILITY, return true
  if (
    invalidCategories.includes(careCategory as CareCategories) &&
    (sectionTypeLower === SectionType.PROVIDER_GROUP?.toLowerCase() ||
      sectionTypeLower === SectionType.FACILITY?.toLowerCase())
  ) {
    return true;
  }

  // Otherwise, use the original logic
  return !invalidCategories.includes(careCategory as CareCategories);
}
